import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Code2, Languages, Sigma } from 'lucide-react';
import {
  verifyMathPassword,
  verifyTheologyPassword,
  getTheologyAccess,
  getMathAccess,
} from '../../services/settingsService';
import styles from './StudyDashboard.module.css';

// Stagger container: orchestrates children timing (identical to Projects page)
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24,
    scale: 0.97,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.48,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const MATH_ACCESS_UNTIL_KEY = 'math-access-until';
const MATH_ACCESS_DURATION = 30 * 60 * 1000;

const STUDY_CARDS = [
  {
    id: 'theology',
    title: 'Theology',
    desc: 'Explore theological studies and resources.',
    icon: BookOpen,
    iconClass: styles.theologyIcon,
    isProtected: true,
    protectedKey: 'theology',
  },
  {
    id: 'cs',
    title: 'CS',
    desc: 'Computer Science fundamentals and coding practice.',
    icon: Code2,
    iconClass: styles.csIcon,
    path: '/study/cs',
  },
  {
    id: 'spanish',
    title: 'Spanish',
    desc: 'Practice Spanish with AI-generated exams and exercises.',
    icon: Languages,
    iconClass: styles.spanishIcon,
    path: '/study/spanish',
  },
  {
    id: 'math',
    title: 'Math',
    desc: 'New Zealand junior and senior maths modules with step-by-step practice.',
    icon: Sigma,
    iconClass: styles.mathIcon,
    isProtected: true,
    protectedKey: 'math',
  },
];

export default function StudyDashboard() {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [pin, setPin] = useState('');
  const [modalStatus, setModalStatus] = useState('idle'); // idle | verifying | error | success
  const [accessConfig, setAccessConfig] = useState({
    theology: { requirePassword: true },
    math: { requirePassword: true },
  });

  // Pre-fetch access configurations on load so clicks respond instantly
  useEffect(() => {
    Promise.all([getTheologyAccess(), getMathAccess()])
      .then(([theo, math]) => {
        setAccessConfig({
          theology: { requirePassword: theo?.requirePassword !== false },
          math: { requirePassword: math?.requirePassword !== false },
        });
      })
      .catch(() => {});
  }, []);

  const protectedModules = {
    theology: {
      title: 'Theology',
      path: '/study/theology',
      icon: 'bi bi-book-half',
      color: '#1976d2',
      background: '#e3f2fd',
      verify: verifyTheologyPassword,
    },
    math: {
      title: 'Math',
      path: '/study/math',
      icon: 'bi bi-calculator',
      color: '#2f8956',
      background: '#e8f5e9',
      verify: verifyMathPassword,
    },
  };

  const handleProtectedClick = (moduleKey, e) => {
    if (e) e.preventDefault();
    const moduleConfig = protectedModules[moduleKey];

    if (moduleKey === 'math') {
      const accessUntil = Number(localStorage.getItem(MATH_ACCESS_UNTIL_KEY) || 0);
      if (accessUntil > Date.now()) {
        navigate(moduleConfig.path);
        return;
      }
      localStorage.removeItem(MATH_ACCESS_UNTIL_KEY);
    }

    // Direct navigation if protection is explicitly disabled
    if (accessConfig[moduleKey]?.requirePassword === false) {
      navigate(moduleConfig.path);
      return;
    }

    // Pop up modal INSTANTLY (0ms lag)
    setPin('');
    setActiveModule(moduleConfig);
    setModalStatus('idle');
    setShowModal(true);

    // Verify background check asynchronously without delaying modal opening
    moduleConfig.verify('')
      .then((result) => {
        if (result.valid && result.requirePassword === false) {
          setShowModal(false);
          navigate(moduleConfig.path);
        }
      })
      .catch(() => {});
  };

  const handlePinSubmit = async () => {
    if (!activeModule) return;
    setModalStatus('verifying');
    const result = await activeModule.verify(pin);
    if (!result.valid) {
      setModalStatus('error');
    } else {
      if (activeModule.path === '/study/math') {
        localStorage.setItem(MATH_ACCESS_UNTIL_KEY, String(Date.now() + MATH_ACCESS_DURATION));
      }
      setModalStatus('success');
      setTimeout(() => {
        setShowModal(false);
        navigate(activeModule.path);
      }, 800);
    }
  };

  return (
    <>
      <div className={`flex flex-col items-center w-full ${styles.container}`}>
        <AnimatePresence mode="popLayout">
          <motion.div
            className={styles.grid}
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            {STUDY_CARDS.map((card) => {
              const IconComponent = card.icon;
              return (
                <motion.div
                  key={card.id}
                  variants={cardVariants}
                  whileHover={{
                    y: -4,
                    boxShadow:
                      "0 18px 40px -10px rgba(47, 137, 86, 0.22), 0 8px 18px -6px rgba(0,0,0,0.12)",
                    transition: { duration: 0.3, ease: "easeOut" },
                  }}
                  className={styles.card}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    if (card.isProtected) {
                      handleProtectedClick(card.protectedKey, e);
                    } else {
                      navigate(card.path);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      if (card.isProtected) {
                        handleProtectedClick(card.protectedKey, e);
                      } else {
                        navigate(card.path);
                      }
                    }
                  }}
                >
                  <div className={`${styles.iconWrap} ${card.iconClass}`}>
                    <IconComponent aria-hidden="true" strokeWidth={1.8} />
                  </div>
                  <h2 className={styles.cardTitle}>{card.title}</h2>
                  <p className={styles.cardDesc}>{card.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>

      {showModal && activeModule && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '2rem',
            width: '340px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{
                width: 40, height: 40, borderRadius: '10px', background: activeModule.background,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: activeModule.color, fontSize: '1.2rem'
              }}>
                <i className={activeModule.icon}></i>
              </div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#333' }}>{activeModule.title}</h3>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#888', marginBottom: '1.25rem' }}>
              Enter the 6-digit PIN to access {activeModule.title} resources.
            </p>
            <input
              type="text"
              maxLength={6}
              value={pin}
              onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setModalStatus('idle'); }}
              style={{
                fontSize: '2rem', letterSpacing: '0.5em', textAlign: 'center',
                width: '100%', padding: '0.5rem', borderRadius: '8px',
                border: `2px solid ${modalStatus === 'error' ? '#e53935' : modalStatus === 'success' ? '#1e8e3e' : '#ddd'}`,
                marginBottom: '1.25rem', outline: 'none'
              }}
              placeholder="000000"
              autoFocus
              onKeyDown={e => e.key === 'Enter' && pin.length === 6 && handlePinSubmit()}
            />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  flex: 1, padding: '0.6rem', borderRadius: '8px', border: '1px solid #ddd',
                  background: '#f5f5f5', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: '#555'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePinSubmit}
                disabled={pin.length !== 6 || modalStatus === 'verifying' || modalStatus === 'success'}
                style={{
                  flex: 1, padding: '0.6rem', borderRadius: '8px', border: 'none',
                  background: modalStatus === 'success' ? '#1e8e3e' : '#1976d2',
                  color: '#fff', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600,
                  opacity: (pin.length !== 6 || modalStatus === 'verifying' || modalStatus === 'success') ? 0.6 : 1
                }}
              >
                {modalStatus === 'verifying' ? 'Verifying...' : modalStatus === 'success' ? 'Unlocking...' : 'Unlock'}
              </button>
            </div>
            {modalStatus === 'error' && (
              <p style={{ color: '#e53935', fontWeight: 600, fontSize: '0.85rem', marginTop: '0.75rem', textAlign: 'center' }}>
                Incorrect PIN, please try again.
              </p>
            )}
            {modalStatus === 'success' && (
              <p style={{ color: '#1e8e3e', fontWeight: 600, fontSize: '0.85rem', marginTop: '0.75rem', textAlign: 'center' }}>
                ✓ Access granted!
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
